import dayjs from 'dayjs';
import _ from 'lodash';
import 'dayjs/locale/fr';
import { Button, Dropdown, Header, Segment } from '@mairistem/composants';
import { Liste, ListeTable, ListeTableActionCsv, ListeTableActionPrint } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import React, { type ReactNode } from 'react';
import { ARRETES } from '@/constants';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import { getArretePrevisionnelPeriodique, getArretes, searchArretes } from '@/routes';
import type { ArreteType } from '@/types';
import { useRegieStore } from '@/zustand';
import TransfertComptableModal from '../TransfertComptableModal';
import ArretePeriodiqueListeTableHeader from './ArretePeriodiqueListeTableHeader';
import ArretePeriodiqueListeTableRow from './ArretePeriodiqueListeTableRow';

dayjs.locale('fr');

type PrevisionnelArreteType = ArreteType & {
  annee: number;
  rang: number;
};

type ArretePeriodiqueListeProps = {
  onGoToPointage: () => void;
  /**
   * Passe à `true` une fois l'arrêté journalier automatique d'entrée de menu terminé. Tant qu'il
   * est faux, le chargement du prévisionnel est retenu (et le spinner reste affiché) pour garantir
   * que le prévisionnel reflète les reçus fraîchement intégrés au journalier.
   */
  arreteJournalierReady?: boolean;
};

const ANNEE_MIN = 2025;

const ArretePeriodiqueListe = (props: ArretePeriodiqueListeProps) => {
  const { onGoToPointage, arreteJournalierReady = true } = props;

  const regie = useRegieStore((state) => state.regie);
  const [annee, setAnnee] = React.useState<number>(dayjs().year());
  const [previsionnel, setPrevisionnel] = React.useState<PrevisionnelArreteType | null>(null);
  const [loadingPrevisionnel, setLoadingPrevisionnel] = React.useState<boolean>(true);
  const [transfertModalArrete, setTransfertModalArrete] = React.useState<ArreteType | null>(null);

  const isAnneeCourante = annee === dayjs().year();

  const anneeOptions = React.useMemo(() => {
    const current = dayjs().year();
    const options = [];
    for (let y = current; y >= ANNEE_MIN; y -= 1) {
      options.push({ key: y, value: y, text: `Année ${y}` });
    }
    return options;
  }, []);

  const fixedParams = React.useMemo(
    () => ({
      type: ARRETES.TYPE_PERIODIQUE,
      // Clé suffixée [] : sans crochets, PHP ne conserve que la dernière valeur
      // de la query string et le filtre `since` est perdu.
      'date[]': [`since(${annee}-01-01)`, `until(${annee}-12-31)`],
      perPage: 500,
    }),
    [annee],
  );

  const {
    items,
    loading: loadingListe,
    search,
    setSorts,
    setPage,
    setSearch,
    reset,
  } = useListeSearchSort({
    active: !_.isNil(regie?.identifiant),
    getItems: getArretes,
    searchItems: searchArretes,
    fixedParams,
    all: true,
  });

  // useListeSearchSort garde `fixedParams` dans une ref, donc un changement d'année
  // ne retrigger pas automatiquement le fetch. On force le rechargement ici après le
  // premier rendu (pattern identique à ArretesJournaliers).
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    reset();
  }, [annee]);

  // Charger le prévisionnel uniquement pour l'année courante
  React.useEffect(() => {
    let cancelled = false;

    // Attendre la fin de l'arrêté journalier automatique d'entrée de menu : le prévisionnel doit
    // refléter les reçus fraîchement intégrés au journalier. loadingPrevisionnel reste à true
    // (valeur initiale) → le spinner reste affiché pendant la création.
    if (!arreteJournalierReady) return () => {};

    if (!regie?.identifiant || !isAnneeCourante) {
      setPrevisionnel(null);
      setLoadingPrevisionnel(false);
      return () => {};
    }

    setLoadingPrevisionnel(true);

    (async () => {
      try {
        const response: any = await request.get(getArretePrevisionnelPeriodique());
        if (cancelled) return;
        // `@mairistem/tools` peut soit envelopper la réponse dans `{ data: ... }` soit la
        // retourner brute. On considère un prévisionnel présent uniquement si l'objet renvoyé
        // (ou son `.data`) contient effectivement les champs attendus (annee + rang).
        const candidate = response && 'data' in response ? response.data : response;
        const isValid =
          candidate && typeof candidate === 'object' && 'annee' in candidate && 'rang' in candidate;
        setPrevisionnel(isValid ? candidate : null);
      } catch {
        // 422 attendu quand aucune activité → pas d'élément prévisionnel
        if (!cancelled) setPrevisionnel(null);
      } finally {
        if (!cancelled) setLoadingPrevisionnel(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [regie?.identifiant, isAnneeCourante, arreteJournalierReady]);

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort) ? null : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
    },
    [setSorts],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handleSearch = React.useCallback(
    (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleAnneeChange = React.useCallback((_e, { value }) => {
    setAnnee(value);
  }, []);

  // La liste affichée = prévisionnel en tête (si présent, année courante, hors recherche)
  // + items chargés. Une recherche active masque le prévisionnel : il ne provient pas
  // de l'API filtrée, on ne peut pas garantir qu'il matche la requête.
  const displayedItems = React.useMemo(() => {
    const base = items || [];
    if (previsionnel && isAnneeCourante && _.isEmpty(search)) {
      return [{ ...previsionnel, isPrevisionnel: true }, ...base];
    }
    return base;
  }, [items, previsionnel, isAnneeCourante, search]);

  // Dernier arrêté (hors prévisionnel) pour conditionner les boutons
  const dernierArrete = React.useMemo(
    () => (_.isEmpty(items) ? null : _.maxBy(items as ArreteType[], (a) => a.date)),
    [items],
  );

  const renderRow = (
    item: ArreteType & { isPrevisionnel?: boolean },
    _invalidate: unknown,
    rowProps: Record<string, unknown>,
  ) => <ArretePeriodiqueListeTableRow item={item} rowProps={rowProps} />;

  // Loading global : on attend à la fois la liste et le prévisionnel
  const loading = loadingListe || loadingPrevisionnel;

  // Bouton conditionnel en bas
  const footerButton = React.useMemo(() => {
    if (loading) return null;

    const titrageEffectue =
      !!dernierArrete?.dateValidationOrdonnateur && !!dernierArrete?.dateValidationPosteComptable;

    if (dernierArrete && !titrageEffectue) {
      const libelleLower = (dernierArrete.libelle || '').toLowerCase();
      return (
        <Button
          positive
          size="large"
          content={`Accéder au transfert comptable de l'${libelleLower}`}
          onClick={() => setTransfertModalArrete(dernierArrete)}
        />
      );
    }

    if (previsionnel) {
      return (
        <Button
          positive
          size="large"
          content={`Accéder au contrôle des règlements de la période ${previsionnel.annee} - n°${previsionnel.rang}`}
          onClick={onGoToPointage}
        />
      );
    }

    return null;
  }, [dernierArrete, previsionnel, loading, onGoToPointage]);

  return (
    <>
      <Segment
        inverted
        secondary
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'transparent',
        }}
      >
        <Segment style={{ borderRadius: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Header as="h1" style={{ marginBottom: '1em' }} textAlign="center">
              <Header.Content>Arrêtés Périodiques</Header.Content>
            </Header>
            <Liste
              title=""
              // @ts-expect-error onSearch est bien supporté
              onSearch={handleSearch}
              loading={loading}
              searchable
              search={{
                value: search,
                placeholder: 'Rechercher un arrêté périodique...',
                clearable: true,
                autoFocus: true,
              }}
            >
              <Liste.Header>
                <Dropdown
                  selection
                  options={anneeOptions}
                  value={annee}
                  onChange={handleAnneeChange}
                  style={{ width: 150, marginTop: '-1em' }}
                />
              </Liste.Header>
              <Liste.Content>
                <div style={{ height: 450 }}>
                  <ListeTable
                    count={_.isEmpty(displayedItems) ? 0 : displayedItems.length}
                    loading={loading}
                    items={displayedItems as ArreteType[]}
                    onSort={handleSort}
                    onLoad={handleLoad}
                    perPage={500}
                    noResultsIcon=""
                    noResultsMessage="Aucun arrêté périodique trouvé pour cette régie"
                  >
                    <ListeTable.Header>
                      <ArretePeriodiqueListeTableHeader />
                    </ListeTable.Header>
                    {renderRow as unknown as ReactNode}
                    <ListeTable.Actions>
                      <ListeTableActionCsv
                        items={(items || []) as ArreteType[]}
                        columns={['libelle', 'date', 'montantTotal']}
                        options={{ filename: 'Arrêtés périodiques' }}
                      />
                      <ListeTableActionPrint
                        printOptions={{
                          documentName: 'Arrêtés périodiques',
                          pageStyle: '[data-name="document"] { display: none !important; }',
                        }}
                      />
                    </ListeTable.Actions>
                  </ListeTable>
                </div>
                {footerButton && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '1em',
                      paddingRight: '1em',
                    }}
                  >
                    {footerButton}
                  </div>
                )}
              </Liste.Content>
            </Liste>
          </div>
        </Segment>
      </Segment>
      {transfertModalArrete && (
        <TransfertComptableModal
          arrete={transfertModalArrete}
          onClose={() => setTransfertModalArrete(null)}
          onArreteUpdate={(updated) => {
            setTransfertModalArrete(updated);
            reset();
          }}
        />
      )}
    </>
  );
};

export default ArretePeriodiqueListe;
